/**
 * In-memory auth token cache + persistence sync.
 *
 * The in-memory pair is the synchronous source of truth used to sign requests
 * (`getAccessToken`). Persistence (OS keychain in Tauri, `localStorage` in the
 * browser) is layered on via `token-storage.ts`: every `setTokens` / `clearTokens`
 * mirrors the change to persistent storage so a reload or app restart can restore
 * the session (`hydrateTokens`). Persistence is best-effort and fire-and-forget —
 * a keychain failure must never block login/logout. Tokens are never logged.
 *
 * Before Phase 7.1 tokens were memory-only (a reload logged the user out); they
 * are now persisted securely. See docs/phase-7-1-token-persistence-report.md.
 */

import { clearPersistedTokens, loadPersistedTokens, persistTokens } from "./token-storage";

interface TokenPair {
  accessToken: string;
  refreshToken: string | null;
}

let tokens: TokenPair | null = null;
const listeners = new Set<() => void>();

/**
 * Monotonic session-identity counter, bumped on every token change (login,
 * logout, refresh). A token refresh started under one generation must NOT apply
 * its result if the generation changed meanwhile — that means a manual logout
 * (or a fresh login) happened during the refresh, and it must win. See
 * `auth-refresh.ts`.
 */
let authGeneration = 0;

export function getAuthGeneration(): number {
  return authGeneration;
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function setTokens(accessToken: string, refreshToken: string | null): void {
  tokens = { accessToken, refreshToken };
  authGeneration++;
  emit();
  // Mirror to persistent storage (best-effort; never blocks the caller).
  void persistTokens(accessToken, refreshToken).catch(() => undefined);
}

export function clearTokens(): void {
  tokens = null;
  authGeneration++;
  emit();
  void clearPersistedTokens().catch(() => undefined);
}

/**
 * Load any persisted tokens into the in-memory cache (startup session restore).
 * Does NOT re-persist. Returns the restored pair, or null if none/already set.
 */
export async function hydrateTokens(): Promise<TokenPair | null> {
  if (tokens) return tokens;
  let persisted: TokenPair | null = null;
  try {
    persisted = await loadPersistedTokens();
  } catch {
    persisted = null;
  }
  if (!persisted) return null;
  tokens = persisted;
  emit();
  return tokens;
}

export function getAccessToken(): string | null {
  return tokens?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return tokens?.refreshToken ?? null;
}

export function hasSession(): boolean {
  return tokens !== null;
}

/** Subscribe to token changes. Returns an unsubscribe function. */
export function subscribeTokens(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
